import { assertNever } from "@impero-com/common/utils/obj";

import {
    AdminEditViewAccessRights,
    EntitiesAccessRightsUpdateChange,
    EntitiesAccessRightsUpdateScope,
    EntityActions,
    EntityId,
    EntityOverview,
    StandardAccessRight,
    UserId,
} from "api/generated/model";

export interface AccessRightChanges {
    /** Users with at least one change, mapped to the number of entities that change for them. */
    entityCountByUser: Map<UserId, number>;
    changedEntityCount: number;
}

/**
 * Mirrors the bulk endpoint: a user and entity pair changes when any right in scope differs from
 * the target, downgrades included.
 */
export function computeAccessRightChanges(
    entities: EntityOverview[],
    userIds: UserId[],
    scope: EntitiesAccessRightsUpdateScope,
    change: EntitiesAccessRightsUpdateChange
): AccessRightChanges {
    const target = targetRight(change);
    const changedPairs = entities.flatMap((entity) => {
        const rightsInScope = accessRightsInScope(entity, scope);
        return userIds
            .filter((userId) =>
                rightsInScope.some(
                    (rights) => currentRight(rights, userId) !== target
                )
            )
            .map((userId) => ({ entityId: entity.id, userId }));
    });

    return {
        entityCountByUser: changedPairs.reduce(
            (counts, { userId }) =>
                counts.set(userId, (counts.get(userId) ?? 0) + 1),
            new Map<UserId, number>()
        ),
        changedEntityCount: new Set<EntityId>(
            changedPairs.map(({ entityId }) => entityId)
        ).size,
    };
}

/** The scopes whose access rights the caller may edit on every one of the entities. */
export function editableScopes(
    actions: EntityActions[]
): EntitiesAccessRightsUpdateScope[] {
    if (actions.length === 0) {
        return [];
    }
    const control = actions.every(({ edit }) => edit.controlAccessRights);
    const risk = actions.every(({ edit }) => edit.riskAccessRights);
    return [
        ...(control ? [EntitiesAccessRightsUpdateScope.control] : []),
        ...(risk ? [EntitiesAccessRightsUpdateScope.risk] : []),
        ...(control && risk ? [EntitiesAccessRightsUpdateScope.both] : []),
    ];
}

function accessRightsInScope(
    entity: EntityOverview,
    scope: EntitiesAccessRightsUpdateScope
): AdminEditViewAccessRights[] {
    switch (scope) {
        case "control":
            return [entity.controlAccessRights];
        case "risk":
            return [entity.riskAccessRights];
        case "both":
            return [entity.controlAccessRights, entity.riskAccessRights];
        default:
            return assertNever(scope);
    }
}

function currentRight(
    rights: AdminEditViewAccessRights,
    userId: UserId
): StandardAccessRight | undefined {
    if (rights.admin.includes(userId)) {
        return "Admin";
    }
    if (rights.edit.includes(userId)) {
        return "Edit";
    }
    if (rights.view.includes(userId)) {
        return "View";
    }
    return undefined;
}

function targetRight(
    change: EntitiesAccessRightsUpdateChange
): StandardAccessRight | undefined {
    switch (change.type) {
        case "grant":
            return change.right;
        case "revoke":
            return undefined;
        default:
            return assertNever(change);
    }
}
